'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Avatar } from '@/components/ui/avatar'
import type { ChatMessage as ChatMessageType, ToolCall } from '@/context/AIContext'

interface ChatMessageProps {
  message: ChatMessageType
  userName?: string
  userImage?: string | null
  /** The case currently open in the Workbench, if any (hides its own deep-link). */
  currentCaseId?: string | null
  /** Navigate to a case in the Workbench. */
  onOpenCase?: (caseId: string) => void
}

// ---------------------------------------------------------------------------
// Tool-result rendering
// ---------------------------------------------------------------------------

interface CaseRowLike {
  case_id?: string
  problem_no?: string
  customer_name?: string
  dispute_type?: string
  disputed_amount?: number
  currency?: string
  status?: string
  root_cause?: string | null
  confidence?: string | null
}

function formatAmount(amount?: number, currency?: string): string {
  if (amount === undefined || amount === null) return '-'
  const n = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return currency ? `${n} ${currency}` : n
}

const STATUS_LABELS: Record<string, string> = {
  needs_attention: 'Needs attention',
  ready: 'Ready',
  running: 'Running',
  in_review: 'In review',
  approved: 'Approved',
  awaiting_evidence: 'Awaiting evidence',
  not_runnable: 'Not runnable',
  rejected: 'Rejected',
}

function statusLabel(status?: string): string {
  if (!status) return ''
  return STATUS_LABELS[status] || status
}

/** A single case row with an optional "Open" deep-link. */
function CaseLine({
  c,
  currentCaseId,
  onOpenCase,
}: {
  c: CaseRowLike
  currentCaseId?: string | null
  onOpenCase?: (caseId: string) => void
}) {
  const canOpen = !!(c.case_id && onOpenCase && c.case_id !== currentCaseId)
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border/50 bg-white px-3 py-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm font-medium text-brand-navy">
          <span className="truncate">{c.problem_no ? `#${c.problem_no}` : 'Case'}</span>
          {c.customer_name && (
            <span className="truncate text-muted-foreground font-normal">· {c.customer_name}</span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
          {c.dispute_type && <span>{c.dispute_type}</span>}
          <span>{formatAmount(c.disputed_amount, c.currency)}</span>
          {c.status && <span>· {statusLabel(c.status)}</span>}
          {c.root_cause && <span>· {c.root_cause}</span>}
          {c.confidence && <span>· {c.confidence}</span>}
        </div>
      </div>
      {canOpen && (
        <button
          type="button"
          onClick={() => onOpenCase!(c.case_id!)}
          className={cn(
            'flex flex-shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium',
            'text-brand-cornflower border border-brand-cornflower/30',
            'hover:bg-brand-cornflower/10 focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50'
          )}
        >
          Open
          <Icons.externalLink className="h-3 w-3" strokeWidth={1.5} />
        </button>
      )}
    </div>
  )
}

/** Renders the results of the tools used to build a message. */
function ToolCallCards({
  toolCalls,
  currentCaseId,
  onOpenCase,
}: {
  toolCalls: ToolCall[]
  currentCaseId?: string | null
  onOpenCase?: (caseId: string) => void
}) {
  return (
    <div className="mt-1 w-full space-y-2 pl-1">
      {toolCalls.map((tc) => {
        const result = (tc.result ?? {}) as Record<string, unknown>

        // search_cases -> list of cases, each with an Open deep-link
        if (tc.name === 'search_cases') {
          const cases = (result.cases as CaseRowLike[]) || []
          if (cases.length === 0) {
            return (
              <ToolChip key={tc.id} icon={Icons.search} label="No matching cases found" />
            )
          }
          return (
            <div key={tc.id} className="space-y-1.5">
              <ToolChip
                icon={Icons.search}
                label={`${cases.length} case${cases.length > 1 ? 's' : ''} found`}
              />
              <div className="space-y-1.5">
                {cases.slice(0, 6).map((c, i) => (
                  <CaseLine key={c.case_id || i} c={c} currentCaseId={currentCaseId} onOpenCase={onOpenCase} />
                ))}
              </div>
            </div>
          )
        }

        // get_case -> single case card with an Open deep-link
        if (tc.name === 'get_case' && result.case_id) {
          return (
            <CaseLine
              key={tc.id}
              c={result as CaseRowLike}
              currentCaseId={currentCaseId}
              onOpenCase={onOpenCase}
            />
          )
        }

        // run_investigation -> action status chip
        if (tc.name === 'run_investigation') {
          const status = result.status as string | undefined
          if (status === 'started') {
            return <ToolChip key={tc.id} icon={Icons.play} label={`Investigation started for #${result.problem_no}`} tone="success" />
          }
          if (status === 'blocked') {
            return <ToolChip key={tc.id} icon={Icons.alertTriangle} label="Case cannot be investigated yet" tone="warning" />
          }
          if (status === 'confirm_required') {
            return <ToolChip key={tc.id} icon={Icons.info} label="Awaiting your confirmation to run" />
          }
          return null
        }

        // submit_case_feedback -> recorded chip
        if (tc.name === 'submit_case_feedback' && result.status === 'recorded') {
          const agree = result.verdict === 'agree'
          return (
            <ToolChip
              key={tc.id}
              icon={agree ? Icons.thumbsUp : Icons.thumbsDown}
              label={`Feedback recorded for #${result.problem_no}`}
              tone="success"
            />
          )
        }

        // Everything else: a subtle "referenced" chip (no raw data dump)
        const friendly: Record<string, string> = {
          get_dashboard_kpis: 'Referenced dashboard KPIs',
          get_case_audit_trail: 'Referenced the case audit trail',
          get_domain_reference: 'Referenced dispute reference',
          list_policies: 'Referenced AI policies',
          explain_page: 'Explained the current page',
        }
        if (friendly[tc.name]) {
          return <ToolChip key={tc.id} icon={Icons.zap} label={friendly[tc.name]} />
        }
        return null
      })}
    </div>
  )
}

function ToolChip({
  icon: Icon,
  label,
  tone = 'default',
}: {
  icon: React.ElementType
  label: string
  tone?: 'default' | 'success' | 'warning'
}) {
  const toneCls =
    tone === 'success'
      ? 'text-emerald-700 border-emerald-200 bg-emerald-50'
      : tone === 'warning'
      ? 'text-amber-700 border-amber-200 bg-amber-50'
      : 'text-brand-cornflower border-brand-cornflower/20 bg-brand-cornflower/5'
  return (
    <div className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium', toneCls)}>
      <Icon className="h-3 w-3" strokeWidth={1.5} />
      <span>{label}</span>
    </div>
  )
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Render inline markdown (bold, code, italic) in text
 */
function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  // Match **bold**, `code`, and *italic*
  const regex = /(\*\*[^*]+\*\*)|(`[^`]+`)|(\*[^*]+\*)/g
  let lastIndex = 0
  let match
  let keyIndex = 0

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const segment = match[0]
    if (segment.startsWith('**') && segment.endsWith('**')) {
      // Bold
      parts.push(
        <strong key={keyIndex++} className="font-semibold text-foreground">
          {segment.slice(2, -2)}
        </strong>
      )
    } else if (segment.startsWith('`') && segment.endsWith('`')) {
      // Code
      parts.push(
        <code
          key={keyIndex++}
          className="px-1.5 py-0.5 bg-muted/50 rounded text-xs font-mono text-brand-navy"
        >
          {segment.slice(1, -1)}
        </code>
      )
    } else if (segment.startsWith('*') && segment.endsWith('*')) {
      // Italic
      parts.push(
        <em key={keyIndex++} className="italic">
          {segment.slice(1, -1)}
        </em>
      )
    }
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

/**
 * Render markdown content with support for headers, lists, blockquotes
 */
function MarkdownContent({ content }: { content: string }) {
  const lines = content.split('\n')
  
  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {lines.map((line, i) => {
        const trimmedLine = line.trim()
        
        // Empty line - add spacing
        if (!trimmedLine) {
          return <div key={i} className="h-1" />
        }
        
        // Headers (##, ###)
        if (trimmedLine.startsWith('### ')) {
          return (
            <h4 key={i} className="font-semibold text-foreground mt-3 first:mt-0">
              {renderInlineMarkdown(trimmedLine.slice(4))}
            </h4>
          )
        }
        if (trimmedLine.startsWith('## ')) {
          return (
            <h3 key={i} className="font-bold text-foreground mt-3 first:mt-0">
              {renderInlineMarkdown(trimmedLine.slice(3))}
            </h3>
          )
        }
        
        // Blockquote
        if (trimmedLine.startsWith('> ')) {
          return (
            <blockquote
              key={i}
              className="pl-3 border-l-2 border-brand-cornflower/50 text-muted-foreground italic"
            >
              {renderInlineMarkdown(trimmedLine.slice(2))}
            </blockquote>
          )
        }
        
        // Bullet points (* or -)
        if (/^[*\-]\s/.test(trimmedLine)) {
          return (
            <div key={i} className="flex gap-2 pl-1">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-cornflower/60 mt-2 flex-shrink-0" />
              <span>{renderInlineMarkdown(trimmedLine.replace(/^[*\-]\s/, ''))}</span>
            </div>
          )
        }
        
        // Numbered lists
        if (/^\d+\.\s/.test(trimmedLine)) {
          const match = trimmedLine.match(/^(\d+)\.\s(.*)/)
          if (match) {
            return (
              <div key={i} className="flex gap-2 pl-1">
                <span className="text-brand-cornflower font-medium min-w-[1.25rem]">
                  {match[1]}.
                </span>
                <span>{renderInlineMarkdown(match[2])}</span>
              </div>
            )
          }
        }
        
        // Regular paragraph
        return <p key={i}>{renderInlineMarkdown(trimmedLine)}</p>
      })}
    </div>
  )
}

export function ChatMessage({ message, userName, userImage, currentCaseId, onOpenCase }: ChatMessageProps) {
  const isUser = message.role === 'user'
  const isLoading = message.isLoading

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        {isUser ? (
          <Avatar
            src={userImage}
            fallback={userName || 'U'}
            size="sm"
          />
        ) : (
          <div className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            'bg-gradient-to-br from-brand-navy to-brand-purple',
            'shadow-soft'
          )}>
            <Icons.sparkles className="h-4 w-4 text-white" strokeWidth={1.5} />
          </div>
        )}
      </div>

      {/* Message Content */}
      <div className={cn(
        'flex max-w-[85%] flex-col gap-1',
        isUser ? 'items-end' : 'items-start'
      )}>
        <div
          className={cn(
            'rounded-2xl px-4 py-3',
            'transition-all duration-200',
            isUser
              ? 'bg-brand-navy text-white rounded-br-md'
              : 'bg-white/90 text-foreground border border-border/50 rounded-bl-md shadow-soft',
            isLoading && 'animate-pulse'
          )}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 py-1">
              <div className="flex gap-1">
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '0ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '150ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm text-brand-muted">Thinking...</span>
            </div>
          ) : isUser ? (
            <p className="text-sm whitespace-pre-wrap leading-relaxed">
              {message.content}
            </p>
          ) : (
            <MarkdownContent content={message.content} />
          )}
        </div>

        {/* Timestamp */}
        <span className={cn(
          'text-[10px] text-muted-foreground/60',
          isUser ? 'pr-1' : 'pl-1'
        )}>
          {formatTime(message.timestamp)}
        </span>

        {/* Tool results */}
        {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
          <ToolCallCards
            toolCalls={message.toolCalls}
            currentCaseId={currentCaseId}
            onOpenCase={onOpenCase}
          />
        )}
      </div>
    </div>
  )
}
